/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Card, Grid, ICON_TROPHY_OUTLINE, Message,
  Skeleton,
  Spacing,
  Stack,
  Text,
  TextSkeleton,
  TypographyName,
} from '@doveit/bricks';
import Markdown from 'react-markdown';
import { Agent, IndividualRank, RankType } from '../../../providers/api/dtos';
import useIndividualRank from '../../hooks/use-individual-rank/useIndividualRank';
import AgentLeaderboardPositionPreview from '../../components/agent-leaderboard-position-preview/AgentLeaderboardPositionPreview';
import { getRankDetails, getRankTitle } from './utils';
import { useAgent } from '../../../hooks/use-agent/useAgent';
import * as styles from './AgentRankWidget.style';
import AgentConversionRateSummaryCard from '../../components/agent-conversion-rate-summary-card/AgentConversionRateSummaryCard';
import AgentConversionRateSummaryCardSkeleton from '../../components/agent-conversion-rate-summary-card/AgentConversionRateSummaryCard.skeleton';
import AgentLeaderboardPositionPreviewSkeleton from '../../components/agent-leaderboard-position-preview/AgentLeaderboardPositionPreview.skeleton';
import { getHygraphAssetUrl } from '../../../utils';

export interface AgentRankWidgetProps {
  agentId: NonNullable<Agent['id']>,
}

export const LOAD_INDIVIDUAL_RANK_ERROR_MESSAGE = 'Non è stato possibile caricare le informazioni sulla classifica agenti.';
export const LOAD_INDIVIDUAL_GOAL_ERROR_MESSAGE = 'Non è stato possibile caricare le informazioni sulle metriche.';

export const AgentRankWidget: React.FC<AgentRankWidgetProps> = ({
  agentId,
}) => {
  const {
    data: individualRank,
    isLoading: isIndividualRankLoading,
    error: individualRankError,
  } = useIndividualRank(agentId);

  const { data: agent, isLoading: isAgentLoading, error: agentError } = useAgent(agentId);

  const rankData = React.useMemo(() => individualRank?.reduce((acc, curr) => {
    acc[curr.rankType] = curr;
    return acc;
  }, {} as Record<RankType, IndividualRank>), [individualRank]);

  const showGeneralRank = React.useMemo(
    () => rankData && (!rankData.ROOKIE || rankData.AGENT.rank <= 20),
    [rankData],
  );

  return (
    <Card aria-label="Classifica agenti">
      <Card.Header
        title="Classifica agenti"
        icon={{ path: ICON_TROPHY_OUTLINE }}
      />
      <Card.Content>
        <Spacing padding={[150, 0, 0]}>
          <Grid gutter={75}>
            {/** Rank */}
            <Grid.Unit size={{ MD: 1 / 2 }}>
              {(isIndividualRankLoading || isAgentLoading) && (
                <Stack gap={200}>
                  <TextSkeleton count={2} typography={TypographyName.BODY} />

                  <div css={styles.rankPreview}>
                    <AgentLeaderboardPositionPreviewSkeleton />
                  </div>
                </Stack>
              )}

              {(individualRankError || agentError) && (
                <Message type="critical" message={LOAD_INDIVIDUAL_RANK_ERROR_MESSAGE} />
              )}

              {(rankData && agent) && (
                <Stack gap={200}>
                  <Text.Body>
                    {individualRank?.map((rankItem) => (
                      <Markdown key={`title-${rankItem.rankType}`}>
                        {getRankTitle(rankItem)}
                      </Markdown>
                    ))}
                  </Text.Body>

                  <div css={styles.rankPreview}>
                    {showGeneralRank && (
                      <AgentLeaderboardPositionPreview
                        rank={rankData.AGENT.rank}
                        rankingType={rankData.AGENT.rankType}
                        name={`${agent.name.name} ${agent.name.surname}`}
                        picture={agent.photoId ? getHygraphAssetUrl(agent.photoId) : undefined}
                        sales={rankData.AGENT.sales}
                        details={getRankDetails(rankData.AGENT)}
                      />
                    )}
                    {!showGeneralRank && (
                      <Spacing padding={[0, 75]}>
                        <AgentLeaderboardPositionPreview
                          rank={rankData.ROOKIE.rank}
                          rankingType={rankData.ROOKIE.rankType}
                          name={`${agent.name.name} ${agent.name.surname}`}
                          picture={agent.photoId ? getHygraphAssetUrl(agent.photoId) : undefined}
                          sales={rankData.ROOKIE.sales}
                          details={getRankDetails(rankData.ROOKIE)}
                        />
                      </Spacing>
                    )}
                  </div>
                </Stack>
              )}
            </Grid.Unit>

            {/** CR Metrics */}
            <Grid.Unit size={{ MD: 1 / 2 }}>
              {(isIndividualRankLoading) && (
                <Stack gap={300}>
                  <Skeleton height="1rem" width="4.5rem" />

                  <Stack gap={150}>
                    <AgentConversionRateSummaryCardSkeleton />
                    <AgentConversionRateSummaryCardSkeleton />
                  </Stack>
                </Stack>
              )}

              {(individualRankError) && (
                <Message type="critical" message={LOAD_INDIVIDUAL_GOAL_ERROR_MESSAGE} />
              )}

              {individualRank && (
                <Stack gap={300}>
                  <Text.Body>
                    <strong>
                      Metriche
                    </strong>
                  </Text.Body>

                  <Stack gap={150}>
                    <AgentConversionRateSummaryCard
                      cr="cr2"
                      metric={individualRank[0].conversions.cr2}
                    />
                    <AgentConversionRateSummaryCard
                      cr="cr3"
                      metric={individualRank[0].conversions.cr3}
                    />
                  </Stack>
                </Stack>
              )}
            </Grid.Unit>
          </Grid>
        </Spacing>
      </Card.Content>
    </Card>
  );
};

export default AgentRankWidget;
